import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import cx from 'classnames';
import { filter, find, findIndex, includes, isEmpty, map } from 'lodash';

import { SupportTextProps, size, varient } from '../Input/types';
import { OptionType } from '../Menu/menu.type';
import Icon, { ICON_TYPES } from '../icons';
import { getCordinates } from './helper';
import Backdrop from '../Backdrop/Backdrop';
import Input from '../Input/Input';

import styles from './dropdown.module.scss';

//! mouse hovered styling remaining

interface DropdownProps {
  options: OptionType[];
  placeholder?: string;
  disabled?: boolean;
  value?: OptionType | string | number;
  onMenuItemClick?: (value: OptionType) => void;
  direction?: 'rtl' | 'ltr';
  variant?: varient;
  size?: size;
  width?: string;
  menuWidth?: string;
  className?: string;
  inputClassName?: string;
  inputWrapperClassName?: string;
  inputContainerClassName?: string;
  label?: string;
  isRequiredField?: boolean;
  prefixIcon?: string | React.ReactNode;
  suffixIcon?: string | React.ReactNode;
  labelPrefixIcon?: string | React.ReactNode;
  labelSuffixIcon?: string | React.ReactNode;
  labelClassName?: string;
  onPrefixIconClick?: () => void;
  onSuffixIconClick?: () => void;
  errors?: {
    [key: string]: string;
  };
  setErrors?: (errors: { [key: string]: string }) => void;
  id?: string;
  validation?: SupportTextProps;
  prefixIconClassName?: string;
  suffixIconClassName?: string;
  labelPrefixIconClassName?: string;
  labelSuffixIconClassName?: string;
  menuClassName?: string;
  menuItemClassName?: string;
  selectedItemClassName?: string;
}

const Dropdown: React.FC<DropdownProps> = (props) => {
  const {
    options,
    onMenuItemClick,
    variant = 'outlined',
    size = 'medium',
    width,
    menuWidth = '100%',
    className,
    inputClassName,
    inputContainerClassName,
    inputWrapperClassName,
    placeholder,
    label,
    disabled,
    isRequiredField,
    prefixIcon,
    suffixIcon,
    labelPrefixIcon,
    labelSuffixIcon,
    labelClassName,
    value,
    onPrefixIconClick,
    onSuffixIconClick,
    direction = 'ltr',
    setErrors,
    errors,
    id = 'dropdown',
    validation,
    prefixIconClassName,
    suffixIconClassName,
    labelPrefixIconClassName,
    labelSuffixIconClassName,
    menuClassName,
    menuItemClassName,
    selectedItemClassName,
  } = props;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchKey, setSearchKey] = useState('');
  const [selectedValue, setSelectedValue] = useState<OptionType>({ value: '', label: '' });
  const [cordinates, setCordinates] = useState({
    top: 0,
    left: 0,
  });

  const filteredOptions = useMemo(() => {
    if (!options) return [];
    return filter(options, (option) =>
      includes(option.label?.toLowerCase(), searchKey.toLowerCase()),
    );
  }, [options, searchKey]);

  //* this will be used for keyboard navigation
  const menuItemsRefs = useMemo(
    () => map(Array(filteredOptions.length).fill(0), () => React.createRef<HTMLDivElement>()),
    [filteredOptions.length],
  );
  const inputRef = useRef<HTMLInputElement>(null); //* this is to focus of input
  const dropdownMenuRef = useRef<HTMLDivElement>(null); //* this is to focus on dropdown menu
  const currentHoveredItem = useRef(-1); //* to keep track the current hoverd item from keyboard

  const triggerRef = useRef<HTMLDivElement>(null); //* this is to get the cordinates of dropdown menu

  //* helper for keyboard navigation
  const handleKeyDownWithScroll = useCallback(
    (offset: number) => {
      if (!dropdownMenuRef.current) return;

      const totlaItemCount = filteredOptions.length;
      const dropdownMenuHeight = dropdownMenuRef.current?.clientHeight || 0;
      const prevItemIndex = currentHoveredItem.current;
      const currentItemIndex =
        (currentHoveredItem.current + offset + totlaItemCount) % totlaItemCount;

      //* increment the currentHoveredItem
      currentHoveredItem.current = currentItemIndex;

      //* remove the styles from the prev item if exists
      if (prevItemIndex !== -1) {
        const prevItemRef = menuItemsRefs[prevItemIndex].current;
        if (prevItemRef) prevItemRef.classList.remove(styles.hovered);
      }

      const currentItemRef = menuItemsRefs[currentItemIndex].current;
      const currentItemHeight = currentItemRef?.clientHeight || 0; // get the height of current item
      const scrollTop = dropdownMenuRef.current?.scrollTop || 0; // get the scroll top of dropdownMenuRef
      const scrollBottom = scrollTop + dropdownMenuHeight; // get the scroll bottom of dropdownMenuRef

      // get the padding top of dropdownMenuRef
      const paddingTop = parseInt(window.getComputedStyle(dropdownMenuRef.current).paddingTop, 10);

      if (currentItemRef) {
        //* add the styles to the current item
        currentItemRef.classList.add(styles.hovered);

        //* scroll the dropdownMenuRef if the current item is not in the view
        if (currentItemRef.offsetTop < scrollTop) {
          dropdownMenuRef.current?.scrollTo({
            top: currentItemRef.offsetTop - paddingTop,
            behavior: 'smooth',
          });
        } else if (currentItemRef.offsetTop + currentItemHeight > scrollBottom) {
          dropdownMenuRef.current?.scrollTo({
            top: currentItemRef.offsetTop + currentItemHeight - dropdownMenuHeight + paddingTop,
            behavior: 'smooth',
          });
        }
      }
    },
    [filteredOptions.length, menuItemsRefs],
  );

  const handleMenuItemClick = useCallback(
    (option: OptionType) => {
      if (onMenuItemClick) onMenuItemClick(option);
      setSelectedValue(option);
      setSearchKey(option.label);
      setIsDropdownOpen(false);
      inputRef.current?.blur();
    },
    [onMenuItemClick],
  );

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleMenuItemClick(selectedValue);
      } else if (e.key === 'ArrowDown') {
        handleKeyDownWithScroll(1);
      } else if (e.key === 'ArrowUp') {
        handleKeyDownWithScroll(-1);
      } else if (e.key === 'Enter') {
        const currentItemIndex = currentHoveredItem.current;
        if (currentItemIndex === -1 || currentItemIndex >= filteredOptions.length) return;

        const currentItemRef = menuItemsRefs[currentHoveredItem.current].current;
        if (currentItemRef) currentItemRef.click();
      }
    },
    [
      filteredOptions.length,
      handleKeyDownWithScroll,
      handleMenuItemClick,
      menuItemsRefs,
      selectedValue,
    ],
  );

  //* subscribe to for keyboard events
  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('keydown', handleKeydown);
    } else {
      document.removeEventListener('keydown', handleKeydown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [isDropdownOpen, handleKeydown, filteredOptions]);

  //i have selected value of dropdown and i want to scroll to that item when user opens the dropdown
  useEffect(() => {
    if (isDropdownOpen) {
      const index = findIndex(filteredOptions, (option) => option.value === selectedValue.value);
      if (index !== -1) {
        // const currentItemRef = menuItemsRefs[index].current;
        // if (currentItemRef) currentItemRef.scrollIntoView({ block: 'start' });

        // scroll dropdownMenuRef to the selected item
        const dropdownMenuHeight = dropdownMenuRef.current?.clientHeight || 0;
        const currentItemRef = menuItemsRefs[index].current;
        const currentItemHeight = currentItemRef?.clientHeight || 0; // get the height of current item

        const paddingTop = parseInt(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          window.getComputedStyle(dropdownMenuRef.current).paddingTop,
          10,
        );
        dropdownMenuRef.current?.scrollTo({
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          top: currentItemRef.offsetTop + currentItemHeight - dropdownMenuHeight + paddingTop,
          behavior: 'smooth',
        });
      }
    }
  }, [isDropdownOpen, filteredOptions, selectedValue, menuItemsRefs]);

  useLayoutEffect(() => {
    if (!dropdownMenuRef.current || !triggerRef.current) return;

    const {
      height: contentHeight,
      width: contentWidth,
      x,
      y,
    } = dropdownMenuRef.current.getBoundingClientRect();
    const { height, width } = triggerRef.current.getBoundingClientRect();

    const { left, top } = getCordinates(height, width, contentHeight, contentWidth, 'bottom', x, y);

    setCordinates({
      top,
      left,
    });
  }, [isDropdownOpen]);

  useEffect(() => {
    if (typeof value === 'string' || typeof value === 'number') {
      const lable = find(options, (option) => option.value === value)?.label;
      setSelectedValue({ value, label: lable || '' });
      setSearchKey(lable || '');
      return;
    }

    if (value) {
      setSelectedValue(value);
      setSearchKey(value.label);
    }
  }, [options, value]);

  return (
    <div
      className={cx(styles.dropdown, className, { [styles.disabled]: disabled })}
      style={{ width }}
    >
      <Backdrop
        setVisible={(value) => {
          handleMenuItemClick(selectedValue);
          setIsDropdownOpen(value);
        }}
        visible={isDropdownOpen}
        isTransparent
      />
      <div ref={triggerRef}>
        <Input
          size={size}
          placeholder={placeholder}
          variant={variant}
          label={label}
          isRequiredField={isRequiredField}
          prefixIcon={prefixIcon}
          suffixIcon={suffixIcon || <Icon icon={ICON_TYPES.CHEVRONDOWN} />}
          labelPrefixIcon={labelPrefixIcon}
          labelSuffixIcon={labelSuffixIcon}
          labelClassName={labelClassName}
          className={inputWrapperClassName}
          inputClassName={inputClassName}
          containerClassName={cx(styles.inputContainer, inputContainerClassName)}
          onPrefixIconClick={onPrefixIconClick}
          onSuffixIconClick={
            onSuffixIconClick ||
            (() => {
              setSearchKey('');
              if (!isDropdownOpen) inputRef?.current?.focus();
              setIsDropdownOpen((prev) => !prev);
            })
          }
          direction={direction}
          setErrors={setErrors}
          errors={errors}
          id={id}
          validation={validation}
          prefixIconClassName={prefixIconClassName}
          suffixIconClassName={suffixIconClassName}
          labelPrefixIconClassName={labelPrefixIconClassName}
          labelSuffixIconClassName={labelSuffixIconClassName}
          onClick={() => {
            setSearchKey('');
            setIsDropdownOpen((prev) => !prev);
          }}
          onChange={setSearchKey}
          value={searchKey}
          ref={inputRef}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isDropdownOpen) {
              setIsDropdownOpen((prev) => !prev);
              setSearchKey('');
            }
          }}
          // onBlur={() => setIsDropdownOpen(false)}
        />
      </div>

      <div
        className={styles.dropdownMenuContainer}
        style={{
          width: menuWidth,
          display: isDropdownOpen ? 'block' : 'none',
          ...cordinates,
        }}
      >
        {isEmpty(filteredOptions) ? (
          <div className={styles.noResult}>
            <p>No result found</p>
          </div>
        ) : (
          <div
            ref={dropdownMenuRef}
            className={cx(styles.dropdownMenu, menuClassName)}
            style={{ width: menuWidth }}
          >
            {map(filteredOptions, (option, index) => (
              <div
                ref={menuItemsRefs[index]}
                key={option.value.toString() + index}
                className={cx(styles.dropdownItem, menuItemClassName, {
                  [cx(styles.selected, selectedItemClassName)]:
                    selectedValue?.value === option.value,
                })}
                onClick={() => handleMenuItemClick(option)}
              >
                <Icon icon={option.icon} />
                <p className={styles.itemLabel}>{option.label}</p>
                <p className={styles.secondaryLabel}>{option.secondaryLabel}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dropdown;
